<script lang="ts">
import type { FilePreviewData } from '#layers/assets/types/file';
import type { AnyItem, GlobalCollectionId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, IdFieldSelection } from '#layers/data-model';
import type { DisplayBlockProps } from '~/types/display-block';

export interface DisplayFileThumbnailProps extends DisplayBlockProps<FilePreviewData | FilePreviewData[]> {
	fieldId: GlobalSingleRelationFieldId;
}

const NEEDED_FILE_FIELDS = new Set(['id', 'fileName', 'mediaType', 'title', 'description']);

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_file_thumbnail.name',
	description: '$t:blocks.display_file_thumbnail.description',
	icon: 'lucide:image',
	category: 'display',
	hidden: true,
	display: {
		fields: (blockProps, ctx) => {
			const field = ctx.getRelationField(blockProps.fieldId);

			if (!field) return ['*'];

			function fileFieldSelection(collectionId: GlobalCollectionId): GlobalPrimitiveFieldId[] | ['*'] {
				const col = ctx.getCollection(collectionId) as
					{ primitiveFields: Array<{ id: GlobalPrimitiveFieldId; apiName: string }> } | undefined;

				if (!col) return ['*'];

				const matched = col.primitiveFields
					.filter((f) => NEEDED_FILE_FIELDS.has(f.apiName))
					.map((f) => f.id);

				return matched.length > 0 ? matched : ['*'];
			}

			// For an m2m relation the field points at the junction, so the file fields live
			// one level deeper, under the junction's toTarget relation.
			if (isM2M(field)) {
				try {
					const { junction, target } = getM2MStructure(field);
					return [{ [blockProps.fieldId]: [{ [junction.toTarget.id]: fileFieldSelection(target.collectionId) }] }];
				}
				catch {
					return [{ [blockProps.fieldId]: ['*'] }] as IdFieldSelection;
				}
			}

			return [{ [blockProps.fieldId]: fileFieldSelection(field.oppositeCollectionId) }] as IdFieldSelection;
		},
	},
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine';
import { getM2MStructure, isM2M } from '#layers/data-model';
import { useSingleRelationField } from '~/composables/use-single-relation-field';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<DisplayFileThumbnailProps>();

;

const MAX_THUMBNAILS = 3;

const field = useSingleRelationField(() => props.fieldId);

const files = computed<FilePreviewData[]>(() => {
	if (!props.value) return [];

	const rawValues = Array.isArray(props.value) ? props.value : [props.value];

	// For an m2m file relation the value is junction rows; unwrap the embedded
	// target file from each (it lives under the junction's toTarget relation).
	if (field.value && isM2M(field.value)) {
		try {
			const toTargetApiName = getM2MStructure(field.value).junction.toTarget.apiName;
			return (rawValues as AnyItem[])
				.map((row) => row?.[toTargetApiName] as FilePreviewData | undefined)
				.filter((file): file is FilePreviewData => !!file);
		}
		catch {
			return [];
		}
	}

	return rawValues;
});

const visibleFiles = computed(() => files.value.slice(0, MAX_THUMBNAILS));
const overflowCount = computed(() => Math.max(0, files.value.length - MAX_THUMBNAILS));
</script>

<template>
	<DDisplayValueEmpty v-if="files.length === 0" />
	<div v-else class="flex h-lh items-center gap-0.5">
		<FileThumbnail
			v-for="(file, index) in visibleFiles"
			:key="file.id ?? index"
			:file
			class="not-first:ms-0.5 not-last:me-0.5"
		/>
		<span v-if="overflowCount > 0" class="ms-1 text-xs text-muted">
			+{{ overflowCount }}
		</span>
	</div>
</template>
